import React, { useState, forwardRef } from "react";
import { HiOutlineEye, HiOutlineEyeOff } from "react-icons/hi";

const PasswordField = forwardRef(({
  icon: Icon,
  value,
  onChange,
  placeholder = "••••••••",
  className = "",
  error = false,
  disabled = false,
  id,
  name,
  ...props
}, ref) => {
  // Encapsulating the visibility state internally keeps parent components clean
  const [showPassword, setShowPassword] = useState(false);

  const toggleVisibility = (e) => {
    e.preventDefault();
    if (!disabled) {
      setShowPassword((prev) => !prev);
    }
  };

  return (
    <div className="relative w-full group">
      {/* Optional Leading Icon */}
      {Icon && (
        <Icon
          className={`absolute left-3.5 top-1/2 -translate-y-1/2 text-lg transition-colors z-10 ${error
              ? "text-red-400"
              : "text-gray-400 group-focus-within:text-emerald-600"
            }`}
          aria-hidden="true"
        />
      )}

      <input
        ref={ref}
        id={id}
        name={name}
        type={showPassword ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        className={`block w-full appearance-none rounded-lg border bg-white py-3 text-sm text-gray-900 shadow-sm transition-all focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 ${Icon ? 'pl-11' : 'pl-4'
          } pr-11 ${error
            ? "border-red-300 placeholder:text-red-300 focus:border-red-500 focus:ring-1 focus:ring-red-500"
            : "border-gray-300 placeholder:text-gray-400 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
          } ${className}`}
        {...props}
      />

      {/* Visibility Toggle Button */}
      <button
        type="button"
        onClick={toggleVisibility}
        onMouseDown={(e) => e.preventDefault()} // Prevents focus from leaving the input
        disabled={disabled}
        tabIndex="-1"
        aria-label={showPassword ? "Hide password" : "Show password"}
        className={`absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-lg transition-colors focus:outline-none ${disabled
            ? "text-gray-300 cursor-not-allowed"
            : "text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:bg-gray-100"
          }`}
      >
        {showPassword ? (
          <HiOutlineEyeOff className="animate-in zoom-in duration-200" aria-hidden="true" />
        ) : (
          <HiOutlineEye className="animate-in zoom-in duration-200" aria-hidden="true" />
        )}
      </button>
    </div>
  );
});

PasswordField.displayName = "PasswordField";

export default PasswordField;